import {
    BaseItem,
    BaseProductItem, BoliaMoney,
    ClickAndCollectStoreInventoryResult,
    DeliverableType,
    PickAndPackInventoryResult,
    STOREClientService,
} from '@/storecontext/shared/api/server-apis';
import useBasket from '@/storecontext/basket/useBasket';
import utilities from '@/shared/utilities.service';
import { computed, ComputedRef, ref, Ref, watch } from 'vue';
import { useSessionStorage } from '@vueuse/core';

export type DeliverableSelectItem = {
    itemId: number;
    sku: string;
    name: string;
    selectable: boolean;
}

export const CHECKOUT_POSTAL_CODE_KEY = 'checkoutPostalCodeKey';
const customerDeliveryPostalCode: Ref<string> = useSessionStorage(CHECKOUT_POSTAL_CODE_KEY, '');
const customerDeliveryFreightPrice: Ref<BoliaMoney> = ref({} as BoliaMoney);

export function useDeliverables() {
    const { basketId, deliverables } = useBasket();

    const hasCustomerDeliverable: ComputedRef<boolean> = computed(() => {
        const customerDeliverable = deliverables.value.find((deliverable) => deliverable.type === DeliverableType.CustomerDelivery);
        return customerDeliverable !== undefined;
    });

    const getClickAndCollectStatus = async(itemIds: number[]): Promise<(ClickAndCollectStoreInventoryResult | undefined)[] | undefined> => {
        const result = STOREClientService.getClickAndCollectStoreInventoryStatus(basketId.value, itemIds).then(utilities.unwrapModel);
        return result || [] as (ClickAndCollectStoreInventoryResult | undefined)[];
    };

    const getPickAndPackStatus = async(itemIds: number[]): Promise<(PickAndPackInventoryResult | undefined)[] | undefined> => {
        const result = STOREClientService.getPickAndPackInventoryStatus(basketId.value, itemIds).then(utilities.unwrapModel);
        return result || [] as (PickAndPackInventoryResult | undefined)[];
    };

    const getSelectItemsFromItems = (items: BaseProductItem[], getSelectable: (item: BaseProductItem) => boolean): DeliverableSelectItem[] => {
        return items.map(item => ({
            itemId: item.id,
            sku: item.sku,
            name: item.name,
            selectable: getSelectable(item),
        }));
    };

    const quantities = (items: BaseItem[]): number => {
        let quantity = 0;
        items.forEach(item => quantity += (item as BaseProductItem).quantity);

        return quantity;
    };

    const resetCustomerDeliverableInfo = (): void => {
        customerDeliveryPostalCode.value = '';
        customerDeliveryFreightPrice.value = {} as BoliaMoney;
    };

    watch(hasCustomerDeliverable, (value) => {
        if (!value) {
            resetCustomerDeliverableInfo();
        }
    });

    return {
        getClickAndCollectStatus,
        getPickAndPackStatus,
        getSelectItemsFromItems,
        quantities,
        resetCustomerDeliverableInfo,
        customerDeliveryPostalCode,
        customerDeliveryFreightPrice,
        hasCustomerDeliverable,
    };
}
